import React, { useState } from 'react';

import { useAppContext } from '../../components/AppContext';
import type { Experience } from '../../types';
import getClientDomain from '../../utils/getClientDomain';
import getZone from '../../utils/getZone';

import type { Analytics, AuthenticationActionOrigin } from './types';
import createAnalytics from './utils/createAnalytics';

interface ProviderProps {
  actionOrigin: AuthenticationActionOrigin;
  experience: Experience;
  children: React.ReactNode;
}

interface State {
  analytics: Analytics;
}

const AnalyticsContext = React.createContext<State | undefined>(undefined);

export const useAnalyticsContext = () => {
  const context = React.useContext(AnalyticsContext);

  if (context === undefined) {
    throw new Error('AnalyticsContext must be used within a AnalyticsProvider');
  }

  return context;
};

export const AnalyticsProvider = (props: ProviderProps) => {
  const { children, ...analyticsProps } = props;
  const context = useAppContext();

  const clientDomain = getClientDomain(context, context.experience);
  const zone = getZone(clientDomain);

  const { locale, brand, language } = context;
  const [analytics] = useState(
    createAnalytics({
      siteCountry: locale,
      brand,
      siteLanguage: language,
      zone,
      isLoggedIn: false,
      ...analyticsProps,
    }),
  );

  return (
    <AnalyticsContext.Provider value={{ analytics }}>
      {children}
    </AnalyticsContext.Provider>
  );
};
